// Notfall-Durchsage und Ein-/Ausschaltzeiten: Typen (Spiegel von apps/core/src/contracts/emergency-power.ts) und Abfragen.
import { api } from './client';
import { useTenantQuery } from './queries';

export type EmergencyTemplate = 'fire' | 'evacuation' | 'general' | 'closing_early' | 'custom';
export type EmergencySound = 'none' | 'alarm' | 'chime';
export type EmergencyTargetType = 'tenant' | 'locations' | 'groups' | 'devices';
export type DeliveryStatus = 'displayed' | 'delivered' | 'pending' | 'offline' | 'cleared';

export interface BroadcastDevice {
  deviceId: string; name: string; locationId: string | null; connectivity: 'online' | 'offline' | 'unknown'; status: DeliveryStatus;
  deliveredAt: string | null; displayedAt: string | null; clearedAt: string | null;
}
export interface Broadcast {
  id: string; template: EmergencyTemplate; title: string; message: string; background: string; textColor: string; blink: boolean;
  sound: EmergencySound; target: { type: EmergencyTargetType; ids: string[] }; status: 'active' | 'ended'; startsAt: string;
  endsAt: string | null; endedAt: string | null; endReason: 'manual' | 'expired' | null; createdBy: string; endedBy: string | null;
  counts: { total: number; delivered: number; displayed: number; offline: number };
}
export interface BroadcastDetail extends Broadcast { devices: BroadcastDevice[] }
export interface StartBroadcast {
  template: EmergencyTemplate; title: string; message: string; background: string; textColor: string; blink: boolean; sound: EmergencySound;
  target: { type: EmergencyTargetType; ids: string[] }; durationMinutes: number | null; confirm: true;
}

export type Weekday = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
export const WEEKDAYS: Weekday[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
export type PowerTargetType = 'device' | 'group' | 'location' | 'tenant';
export interface PowerWindow { weekdays: Weekday[]; on: string; off: string }
export interface PowerException { date: string; dateUntil?: string | null; mode: 'on' | 'off'; on?: string | null; off?: string | null; label?: string | null }
export interface PowerSchedule {
  id: string; targetType: PowerTargetType; targetId: string | null; enabled: boolean; weekly: PowerWindow[]; exceptions: PowerException[];
  rowVersion: number; updatedAt: string;
}
export interface DevicePower {
  timezone: string; schedule: PowerSchedule | null; via: PowerTargetType | null; override: { mode: 'on' | 'off'; until: string } | null;
  state: { on: boolean; source: 'always' | 'schedule' | 'exception' | 'override'; nextChangeAt: string | null };
  reported: { display: 'on' | 'off'; at: string } | null;
}

export const qkEp = {
  broadcasts: ['emergency-broadcasts'] as const,
  broadcast: (id: string) => ['emergency-broadcasts', id] as const,
  powerSchedules: ['power-schedules'] as const,
  devicePower: (id: string) => ['devices', id, 'power'] as const,
};

/** Durchsagen; solange eine aktiv ist, alle 2 s (Quittungen „angezeigt auf 7/8" live). */
export const useBroadcasts = (opts: { enabled?: boolean } = {}) => useTenantQuery(qkEp.broadcasts,
  async () => (await api.get<{ items: Broadcast[] }>('/emergency/broadcasts?limit=30')).items,
  { refetchInterval: (d) => (d?.some((b) => b.status === 'active') ? 2000 : 15_000), ...(opts.enabled !== undefined ? { enabled: opts.enabled } : {}) });
export const useBroadcast = (id: string | null) => useTenantQuery(qkEp.broadcast(id ?? ''), () => api.get<BroadcastDetail>(`/emergency/broadcasts/${id}`),
  { enabled: !!id, refetchInterval: (d) => (d?.status === 'active' ? 2000 : false) });
export const usePowerSchedules = () => useTenantQuery(qkEp.powerSchedules, async () => (await api.get<{ items: PowerSchedule[] }>('/power-schedules')).items);
export const useDevicePower = (id: string) => useTenantQuery(qkEp.devicePower(id), () => api.get<DevicePower>(`/devices/${id}/power`), { refetchInterval: 15_000 });

/** Vorlagen: Farben, Ton, Blinken; Texte aus der Übersetzung (emergency.tpl.<id>.title/message). */
export const TEMPLATES: Record<EmergencyTemplate, { background: string; textColor: string; sound: EmergencySound; blink: boolean }> = {
  fire: { background: '#B91C1C', textColor: '#FFFFFF', sound: 'alarm', blink: true },
  evacuation: { background: '#C2410C', textColor: '#FFFFFF', sound: 'alarm', blink: true },
  general: { background: '#0B1F3A', textColor: '#FFFFFF', sound: 'chime', blink: false },
  closing_early: { background: '#1E3A8A', textColor: '#FFFFFF', sound: 'chime', blink: false },
  custom: { background: '#0B1F3A', textColor: '#FFFFFF', sound: 'none', blink: false },
};
